import { useState } from 'react'
import { storage } from '~config/firebase'
import {
    ref,
    getDownloadURL,
    uploadString,
    deleteObject,
} from 'firebase/storage'
import { getStoragePathFromUrl } from '~utils/storage-helpers'

type UseStorageType = {
    upload: (
        path: string,
        newName: string,
        dataURL: string,
        oldFileUrl?: string
    ) => Promise<string>
    uploading: boolean
}

function getDataUrlContentType(dataURL: string): string | undefined {
    const match = /^data:([^;]+);/i.exec(dataURL)
    return match?.[1]
}

export function useStorage(): UseStorageType {
    const [loading, setLoading] = useState<boolean>(false)

    async function upload(
        path: string,
        newName: string,
        dataURL: string,
        oldFileUrl?: string
    ): Promise<string> {
        setLoading(true)
        try {
            if (!dataURL.startsWith('data:')) {
                throw new Error('Upload requires a data URL')
            }

            const safeName = newName.trim() || 'logo.png'
            const uploadedPath = `${path}/${safeName}`
            const uploadRef = ref(storage, uploadedPath)
            const contentType = getDataUrlContentType(dataURL)
            await uploadString(
                uploadRef,
                dataURL,
                'data_url',
                contentType ? { contentType } : undefined,
            )
            const downloadUrl = await getDownloadURL(uploadRef)

            if (!downloadUrl) {
                throw new Error('Error uploading file')
            }

            if (oldFileUrl && oldFileUrl.includes('firebasestorage')) {
                const deletePath = getStoragePathFromUrl(oldFileUrl)
                if (deletePath && deletePath !== uploadedPath) {
                    try {
                        await deleteObject(ref(storage, deletePath))
                    } catch (error) {
                        console.error(
                            '(upload) Failed to delete previous file',
                            error,
                        )
                    }
                }
            }

            setLoading(false)
            return downloadUrl
        } catch (error: any) {
            console.error('(upload) Error uploading file: ', error)
            setLoading(false)
            return ''
        }
    }

    return {
        upload,
        uploading: loading,
    }
}
